import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Card, Modal, Badge, ListGroup, Row, Col, Form } from 'react-bootstrap';
import { HomeOutlined, EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import { LimsFeedbackModal } from '../../components/LimsFeedbackModal';
import '../MemberManagement.css';
import '../../styles/resourceManagement.css';
import {
  getReservations,
  approveReservation,
  Reservation,
  getPendingApprovalApplications,
  getProcessedApprovalApplications,
  getMyApprovalApplications,
  getFinanceReimbursementApplications,
  approveApprovalApplication,
  confirmMerchantPrice,
  withdrawApprovalApplication
} from '../../services/api';
import { resolveUploadAssetUrl } from '../../utils/resolveUploadAssetUrl';
import { DEFAULT_REAGENT_IMAGE, DEFAULT_CONSUMABLE_IMAGE } from '../../constants/defaultImages';
import { downloadFinanceAttachmentsZip } from '../../utils/downloadFinanceAttachmentsZip';
import {
  literatureNotificationService,
  LiteratureNotification
} from '../../services/literature/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import {
  getDemoApprovalApplicationsForManagement,
  getDemoLiteratureShareInvites,
  getDemoReservationsForApproval
} from '../../demo/demoBrowseMocks';

const parseResponsiblePersons = (value?: string): string[] => {
  if (!value) return [];
  return value.split(/[、,，;\s]+/).map(item => item.trim()).filter(Boolean);
};

const resolveUploadedImageUrl = (url?: string) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/uploads/')) return `/api${url}`;
  return url;
};

interface ApprovalApplication {
  key: string;
  title: string;
  category:
    | '物品申领'
    | '耗材申请'
    | '试剂申请'
    | '用章申请'
    | '物品报修'
    | '请假'
    | '测试申请'
    | '出差申请'
    | '财务报销';
  status: '待审批' | '一级审批中' | '二级审批中' | '三级审批中' | '已通过' | '已驳回' | '已撤回';
  applicant: string;
  applyTime: string;
  priority: '普通' | '紧急' | '特急';
  reason: string;
  reasonImages?: Array<{ name: string; url: string }>;
  selectedTeacherApprovers?: Array<{ id: string; name: string }>;
  selectedFinanceApprovers?: Array<{ id: string; name: string }>;
  applicantId?: string;
  totalAmount?: number;
  financeReimbursement?: {
    claimAmount?: number;
    currency?: string;
    expenseType?: string;
    remark?: string;
    attachments?: Array<{ name: string; url: string }>;
  };
  // 试剂申请字段
  reagentProjects?: Array<{
    category: string;
    image?: string;
    name?: string;
    cas: string;
    catalogNumber?: string;
    vendor: string;
    specification: string;
    unit: string;
    stockQuantity: string;
    storageLocation: string;
    purchaseDate: string;
    purchaseChannel: string;
    amount: string;
    price?: string;
    total?: string;
    usage?: string;
  }>;
  // 耗材申请字段
  consumableProjects?: Array<{
    image?: string;
    name: string;
    model: string;
    brand?: string;
    unit: string;
    stockQuantity: string;
    storageLocation: string;
    purchaseDate: string;
    purchaseChannel: '网购' | '其他';
    purchaseLink?: string;
    supplier?: string;
    amount: string;
    price?: string;
    total?: string;
    usage?: string;
  }>;
  // 请假申请字段
  leaveStart?: string;
  leaveEnd?: string;
  // 测试申请字段
  testPlatform?: string;
  testProjects?: Array<{
    project: string;
    unitPrice: string;
    quantity: string;
    totalAmount: string;
  }>;
  testTotalAmount?: string;
  // 出差申请字段
  destination?: string;
  tripStartDate?: string;
  tripEndDate?: string;
  tripDays?: string;
  tripPurpose?: string;
  tripBudget?: string;
  // 审批记录
  approvalRecords?: Array<{
    node: string;
    approver: string;
    result: string;
    time: string;
  }>;
  merchantOrder?: {
    applyChannel?: 'internal' | 'merchant';
    merchantId?: string;
    merchantName?: string;
    orderStatus?: string;
    originalAmount?: number;
    quotedAmount?: number | null;
    pendingQuotedAmount?: number | null;
    shippingAddress?: string;
    recipientName?: string;
    recipientPhone?: string;
    trackingNumbers?: Array<{ number: string }>;
  };
}

const SHARE_INVITE_TYPES = new Set<LiteratureNotification['type']>([
  'shared_folder_invite',
  'shared_experiment_notebook_invite'
]);

type ApprovalMgmtTableRow =
  | { kind: 'approval'; application: ApprovalApplication }
  | { kind: 'reservation'; reservation: Reservation };

type ApprovalTypeFilterValue =
  | '全部'
  | '仪器预约'
  | '财务报销'
  | ApprovalApplication['category'];

const APPROVAL_TYPE_FILTER_OPTIONS: ApprovalTypeFilterValue[] = [
  '全部',
  '耗材申请',
  '试剂申请',
  '用章申请',
  '物品报修',
  '请假',
  '测试申请',
  '出差申请',
  '财务报销',
  '物品申领',
  '仪器预约'
];

const ApprovalManagement: React.FC = () => {
  const { hash } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const reservationDeepLinkHandledRef = useRef<string | null>(null);
  const applicationDeepLinkHandledRef = useRef<string | null>(null);
  const { isDemoBrowseOnly, username, realName, user } = useAuth();

  // 审批申请数据（分离“待我审批”与“我发起的申请”，避免列表串数据）
  const [pendingApplications, setPendingApplications] = useState<ApprovalApplication[]>([]);
  const [processedApplications, setProcessedApplications] = useState<ApprovalApplication[]>([]);
  const [myApplications, setMyApplications] = useState<ApprovalApplication[]>([]);
  const [financeApplications, setFinanceApplications] = useState<ApprovalApplication[]>([]);

  // 申请相关状态
  const [selectedTodo, setSelectedTodo] = useState<ApprovalApplication | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [priceConfirmMode, setPriceConfirmMode] = useState(false);

  // 审批管理相关状态
  const [approvalMainTab, setApprovalMainTab] = useState('todo');
  const [approvalTypeFilter, setApprovalTypeFilter] = useState<ApprovalTypeFilterValue>('全部');
  /** 深链 applicationId：避免首屏空列表时误报「未找到」 */
  const [deepLinkListsReady, setDeepLinkListsReady] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState<string>('');
  const [approvalTargetKey, setApprovalTargetKey] = useState<string | null>(null);
  const [financeZipDownloading, setFinanceZipDownloading] = useState(false);

  // 仪器预约相关状态
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [approvalForm, setApprovalForm] = useState({
    status: '已批准' as '已批准' | '已拒绝',
    approvalComment: ''
  });
  const [feedbackModal, setFeedbackModal] = useState<{ title: string; message: string } | null>(null);

  const [shareInvites, setShareInvites] = useState<LiteratureNotification[]>([]);
  const [shareInvitesLoading, setShareInvitesLoading] = useState(false);

  const fetchShareInvites = useCallback(async () => {
    setShareInvitesLoading(true);
    try {
      if (isDemoBrowseOnly) {
        setShareInvites(
          getDemoLiteratureShareInvites().filter((n) => n.status === 'pending' && SHARE_INVITE_TYPES.has(n.type))
        );
        return;
      }
      const list = await literatureNotificationService.getList({ status: 'pending', limit: 80 });
      setShareInvites(
        list.filter((n) => n.status === 'pending' && SHARE_INVITE_TYPES.has(n.type))
      );
    } catch {
      setShareInvites([]);
    } finally {
      setShareInvitesLoading(false);
    }
  }, [isDemoBrowseOnly]);

  const showFeedback = (message: string, title = '提示') => {
    setFeedbackModal({ title, message });
  };

  const handleDownloadFinanceAttachmentsZip = async () => {
    if (!selectedTodo?.financeReimbursement?.attachments?.length) return;
    setFinanceZipDownloading(true);
    try {
      await downloadFinanceAttachmentsZip({
        applicant: selectedTodo.applicant,
        applyTime: selectedTodo.applyTime,
        attachments: selectedTodo.financeReimbursement.attachments
      });
    } catch (e) {
      showFeedback((e as Error).message || '打包下载失败', '下载失败');
    } finally {
      setFinanceZipDownloading(false);
    }
  };

  // 审批管理辅助函数
  const getStatusColor = (status: string) => {
    switch (status) {
      case '待审批': return 'warning';
      case '一级审批中':
      case '二级审批中':
      case '三级审批中':
        return 'warning';
      case '已通过': return 'success';
      case '已驳回': return 'danger';
      case '已撤回': return 'secondary';
      default: return 'secondary';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case '特急': return 'danger';
      case '紧急': return 'warning';
      case '普通': return 'info';
      default: return 'secondary';
    }
  };

  const getCategoryMaterialIcon = (category: string) => {
    switch (category) {
      case '试剂申请':
        return 'science';
      case '耗材申请':
        return 'inventory_2';
      case '用章申请':
        return 'approval';
      case '物品报修':
        return 'build';
      case '请假':
        return 'calendar_today';
      case '测试申请':
        return 'biotech';
      case '出差申请':
        return 'flight_takeoff';
      case '物品申领':
        return 'shopping_cart';
      case '财务报销':
        return 'receipt_long';
      default:
        return 'description';
    }
  };

  const formatRelativeApplyTime = (applyTime: string) => {
    const t = new Date(applyTime.replace(/-/g, '/')).getTime();
    if (Number.isNaN(t)) return applyTime;
    const diff = Date.now() - t;
    const h = Math.floor(diff / 3600000);
    if (diff < 0) return applyTime;
    if (h < 1) return '刚刚提交';
    if (h < 24) return `${h} 小时前`;
    const d = Math.floor(h / 24);
    return `${d} 天前`;
  };

  const getApplicationSubtitle = (app: ApprovalApplication) => {
    if (app.category === '试剂申请' && app.reagentProjects?.[0]) {
      const p = app.reagentProjects[0];
      return `${p.vendor} · CAS ${p.cas}`;
    }
    if (app.category === '耗材申请' && app.consumableProjects?.[0]) {
      return app.consumableProjects[0].name;
    }
    if (app.category === '测试申请' && app.testPlatform) {
      return app.testPlatform;
    }
    if (app.category === '财务报销') {
      const n = app.totalAmount ?? app.financeReimbursement?.claimAmount;
      if (n !== undefined && n !== null && String(n) !== '') {
        const cur = app.financeReimbursement?.currency || 'CNY';
        const sym = cur === 'USD' ? '$' : '¥';
        return `${sym}${Number(n).toFixed(2)}${app.financeReimbursement?.expenseType ? ` · ${app.financeReimbursement.expenseType}` : ''}`;
      }
    }
    if (app.reason) return app.reason.length > 56 ? `${app.reason.slice(0, 56)}…` : app.reason;
    return '—';
  };

  const canActOnApplication = (application: ApprovalApplication) =>
    ['待审批', '一级审批中', '二级审批中', '三级审批中'].includes(application.status) ||
    application.merchantOrder?.orderStatus === 'price_pending';

  const getStatusDisplay = (application: ApprovalApplication) => {
    if (application.merchantOrder?.orderStatus === 'price_pending') {
      return '商家改价审批中';
    }
    if (
      application.merchantOrder?.applyChannel === 'merchant' &&
      application.status === '已通过' &&
      application.merchantOrder.orderStatus
    ) {
      const map: Record<string, string> = {
        pending_accept: '待商家接单',
        awaiting_shipment: '商家待发货',
        shipped: '商家已发货',
        rejected: '商家已拒单'
      };
      return map[application.merchantOrder.orderStatus] || application.status;
    }
    return application.status;
  };

  const handleApprove = (application: ApprovalApplication) => {
    setApprovalTargetKey(application.key);
    setApprovalComment('');
    setPriceConfirmMode(application.merchantOrder?.orderStatus === 'price_pending');
    setShowApprovalModal(true);
  };

  const handleReject = (application: ApprovalApplication) => {
    setApprovalTargetKey(application.key);
    setApprovalComment('');
    setPriceConfirmMode(application.merchantOrder?.orderStatus === 'price_pending');
    setShowApprovalModal(true);
  };

  const handleWithdraw = (application: ApprovalApplication) => {
    const run = async () => {
      if (isDemoBrowseOnly) {
        setMyApplications((prev) =>
          prev.map((item) => (item.key === application.key ? { ...item, status: '已撤回' as const } : item))
        );
        showFeedback(`已撤回申请：${application.title}（演示模式，未提交服务器）`, '撤回成功');
        return;
      }
      const response = await withdrawApprovalApplication(application.key);
      if (!response.success) {
        showFeedback(response.error || '撤回失败，请稍后重试。', '撤回失败');
        return;
      }
      setMyApplications((prev) =>
        prev.map((item) => (item.key === application.key ? { ...item, status: '已撤回' } : item))
      );
      void fetchFinanceApplications();
      showFeedback(`已撤回申请：${application.title}`, '撤回成功');
    };
    run();
  };

  // 图片预览函数
  // 处理审批操作
  const handleApprovalAction = async (applicationId: string, result: '通过' | '驳回', comment?: string) => {
    const application = [...pendingApplications, ...myApplications].find(app => app.key === applicationId);
    if (!application) return;

    try {
      if (isDemoBrowseOnly) {
        const nextStatus = result === '通过' ? ('已通过' as const) : ('已驳回' as const);
        setPendingApplications((prev) =>
          prev.map((item) => (item.key === applicationId ? { ...item, status: nextStatus } : item))
        );
        setMyApplications((prev) =>
          prev.map((item) => (item.key === applicationId ? { ...item, status: nextStatus } : item))
        );
        showFeedback(`审批「${result}」已更新（演示模式，未提交服务器）。`, '演示');
        setShowApprovalModal(false);
        setApprovalComment('');
        setApprovalTargetKey(null);
        return;
      }
      const response = priceConfirmMode
        ? await confirmMerchantPrice(applicationId, { result, comment })
        : await approveApprovalApplication(applicationId, { result, comment });
      if (!response.success) {
        showFeedback(response.error || '审批处理失败，请重试。', '操作失败');
        return;
      }
      await Promise.all([
        fetchPendingApplications(),
        fetchProcessedApplications(),
        fetchMyApplications(),
        fetchFinanceApplications()
      ]);
      window.dispatchEvent(new CustomEvent('approvalStatusUpdated'));
      showFeedback(
        priceConfirmMode
          ? result === '通过'
            ? '改价审批已通过。'
            : '已驳回改价申请。'
          : `审批「${result}」已生效。`,
        '审批成功'
      );
      setShowApprovalModal(false);
      setApprovalComment('');
      setApprovalTargetKey(null);
      setPriceConfirmMode(false);
    } catch (error) {
      console.error('审批处理失败:', error);
      showFeedback('审批处理失败，请重试。', '操作失败');
    }
  };

  const handleImagePreview = (imageUrl: string) => {
    setModalImageUrl(resolveUploadedImageUrl(imageUrl));
    setShowImageModal(true);
  };

  const renderReasonImages = (application: ApprovalApplication) => {
    if (!application.reasonImages?.length) return null;
    return (
      <div className="mb-3">
        <strong>原因图片：</strong>
        <div className="d-flex flex-wrap gap-2 mt-2">
          {application.reasonImages.map((image, index) => (
            <button
              key={`${image.url}-${index}`}
              type="button"
              className="border rounded p-1 bg-white"
              onClick={() => handleImagePreview(image.url)}
              aria-label={`预览${image.name || `原因图片 ${index + 1}`}`}
            >
              <img
                src={resolveUploadedImageUrl(image.url)}
                alt={image.name || `原因图片 ${index + 1}`}
                style={{ width: 100, height: 100, objectFit: 'cover', display: 'block' }}
              />
            </button>
          ))}
        </div>
      </div>
    );
  };

  // 获取预约数据
  const fetchReservations = async () => {
    try {
      if (isDemoBrowseOnly) {
        setReservations(
          getDemoReservationsForApproval({
            username: username || localStorage.getItem('username') || '',
            realName: realName || localStorage.getItem('realName') || ''
          })
        );
        return;
      }
      const response = await getReservations();
      if (response.success) {
        setReservations(response.data || []);
      }
    } catch (error) {
      console.error('获取预约数据失败:', error);
    }
  };

  const mapApplication = (app: any) =>
    ({
      key: String(app._id || app.key || ''),
      title: app.title || '',
      category: app.category,
      status: app.status,
      applicant: app.applicant || '',
      applicantId: app.applicantId != null ? String(app.applicantId) : '',
      applyTime: app.applyTime || app.appliedAt || app.createdAt || '',
      priority: app.priority || '普通',
      reason: app.reason || '',
      reasonImages: app.reasonImages || app.applicationData?.reasonImages || [],
      selectedTeacherApprovers:
        app.selectedTeacherApprovers || app.applicationData?.selectedTeacherApprovers || [],
      selectedFinanceApprovers:
        app.selectedFinanceApprovers || app.applicationData?.selectedFinanceApprovers || [],
      reagentProjects: app.reagentProjects || app.applicationData?.reagentProjects || [],
      consumableProjects: app.consumableProjects || app.applicationData?.consumableProjects || [],
      leaveStart: app.leaveStart || app.applicationData?.leaveStart,
      leaveEnd: app.leaveEnd || app.applicationData?.leaveEnd,
      destination: app.destination || app.applicationData?.destination,
      tripStartDate: app.tripStartDate || app.applicationData?.tripStartDate,
      tripEndDate: app.tripEndDate || app.applicationData?.tripEndDate,
      tripDays: app.tripDays || app.applicationData?.tripDays,
      tripPurpose: app.tripPurpose || app.applicationData?.tripPurpose,
      tripBudget: app.tripBudget || app.applicationData?.tripBudget,
      testPlatform: app.testPlatform || app.applicationData?.testPlatform,
      testProjects: app.testProjects || app.applicationData?.testProjects || [],
      testTotalAmount: app.testTotalAmount || app.applicationData?.testTotalAmount,
      totalAmount: app.totalAmount ?? app.applicationData?.totalAmount,
      financeReimbursement: app.financeReimbursement || app.applicationData?.financeReimbursement,
      approvalRecords: app.approvalRecords || [],
      merchantOrder: app.merchantOrder || app.applicationData?.merchantOrder || undefined
    } as ApprovalApplication);

  const fetchPendingApplications = async () => {
    if (isDemoBrowseOnly) {
      return;
    }
    try {
      const response = await getPendingApprovalApplications();
      if (!response.success || !Array.isArray(response.data)) {
        setPendingApplications([]);
        return;
      }
      const mapped = response.data.map(mapApplication);
      setPendingApplications(mapped);
    } catch {
      setPendingApplications([]);
    }
  };

  const fetchMyApplications = async () => {
    if (isDemoBrowseOnly) {
      const mapped = getDemoApprovalApplicationsForManagement().map(mapApplication) as ApprovalApplication[];
      setMyApplications(mapped);
      return;
    }
    try {
      const response = await getMyApprovalApplications();
      if (!response.success || !Array.isArray(response.data)) {
        setMyApplications([]);
        return;
      }
      const mapped = response.data.map(mapApplication) as ApprovalApplication[];
      setMyApplications(mapped);
    } catch {
      setMyApplications([]);
    }
  };

  const fetchProcessedApplications = async () => {
    if (isDemoBrowseOnly) {
      const mapped = getDemoApprovalApplicationsForManagement().map(mapApplication) as ApprovalApplication[];
      setProcessedApplications(mapped.filter((app) => app.status === '已通过' || app.status === '已驳回'));
      return;
    }
    try {
      const response = await getProcessedApprovalApplications();
      if (!response.success || !Array.isArray(response.data)) {
        setProcessedApplications([]);
        return;
      }
      const mapped = response.data.map(mapApplication) as ApprovalApplication[];
      setProcessedApplications(mapped);
    } catch {
      setProcessedApplications([]);
    }
  };

  const fetchFinanceApplications = async () => {
    if (isDemoBrowseOnly) {
      const rows = getDemoApprovalApplicationsForManagement().filter((x) => x.category === '财务报销');
      setFinanceApplications(rows.map(mapApplication) as ApprovalApplication[]);
      return;
    }
    try {
      const response = await getFinanceReimbursementApplications();
      if (!response.success || !Array.isArray(response.data)) {
        setFinanceApplications([]);
        return;
      }
      const mapped = response.data.map(mapApplication) as ApprovalApplication[];
      setFinanceApplications(mapped);
    } catch {
      setFinanceApplications([]);
    }
  };

  // 审批预约
  const handleApproveReservation = async () => {
    if (!selectedReservation) return;

    try {
      if (isDemoBrowseOnly) {
        const id = selectedReservation._id;
        const next = approvalForm.status === '已批准' ? '已批准' : '已拒绝';
        setReservations((prev) =>
          prev.map((r) => (r._id === id ? { ...r, status: next as Reservation['status'] } : r))
        );
        showFeedback('仪器预约审批已更新（演示模式，未提交服务器）。', '演示');
        setShowReservationModal(false);
        setSelectedReservation(null);
        setApprovalForm({ status: '已批准', approvalComment: '' });
        return;
      }
      const response = await approveReservation(
        selectedReservation._id!,
        approvalForm.status,
        approvalForm.approvalComment
      );
      if (response.success) {
        showFeedback('仪器预约审批已提交。', '审批完成');
        await fetchReservations();
        setShowReservationModal(false);
        setSelectedReservation(null);
        setApprovalForm({ status: '已批准', approvalComment: '' });
      } else {
        showFeedback(response.error || '预约审批失败，请重试。', '操作失败');
      }
    } catch (error) {
      showFeedback('网络错误，请稍后重试。', '操作失败');
    }
  };

  // 获取预约状态颜色
  const getReservationStatusColor = (status: string) => {
    switch (status) {
      case '待审核': return 'warning';
      case '已批准': return 'success';
      case '已拒绝': return 'danger';
      case '使用中': return 'info';
      case '已完成': return 'secondary';
      case '已取消': return 'dark';
      default: return 'secondary';
    }
  };

  // 格式化时间
  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('zh-CN');
  };

  // 仪器预约发起时间（兼容历史数据无 createdAt 的情况）
  const formatReservationApplyTime = (reservation: Reservation) => {
    const applyTime = reservation.createdAt || reservation.startTime;
    return formatDateTime(applyTime);
  };

  const canCurrentUserApproveReservation = (reservation: Reservation) => {
    const instrument = typeof reservation.instrumentId === 'object' ? reservation.instrumentId : null;
    const responsiblePersons = parseResponsiblePersons(instrument?.responsiblePerson);
    if (!responsiblePersons.length) return false;
    const currentUsername = localStorage.getItem('username') || '';
    const currentRealName = localStorage.getItem('realName') || '';
    return responsiblePersons.includes(currentUsername) || responsiblePersons.includes(currentRealName);
  };

  // 组件加载时获取预约数据
  useEffect(() => {
    void fetchReservations();
    void fetchPendingApplications();
    void fetchProcessedApplications();
    void fetchMyApplications();
    void fetchFinanceApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoBrowseOnly, username, realName]);

  useEffect(() => {
    setDeepLinkListsReady(false);
    const id = window.setTimeout(() => setDeepLinkListsReady(true), 1200);
    return () => window.clearTimeout(id);
  }, [isDemoBrowseOnly, username, realName]);

  useEffect(() => {
    if (approvalMainTab === 'todo') void fetchShareInvites();
  }, [approvalMainTab, fetchShareInvites]);

  useEffect(() => {
    if (approvalMainTab !== 'todo' || hash !== '#am-share-invites') return;
    const el = document.getElementById('am-share-invites');
    if (!el) return;
    window.requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [approvalMainTab, hash, shareInvitesLoading]);

  /** 从通知链接进入：?tab=… & reservationId=… → 仪器预约 Tab 并打开对应弹窗 */
  useEffect(() => {
    const rid = searchParams.get('reservationId');
    if (!rid) {
      reservationDeepLinkHandledRef.current = null;
    }

    const tab = searchParams.get('tab');
    if (tab === 'todo' || tab === 'done' || tab === 'created') {
      setApprovalMainTab(tab);
    }
    if (tab === 'reservations') {
      setApprovalMainTab('todo');
      setApprovalTypeFilter('仪器预约');
    }
    if (tab === 'finance') {
      setApprovalMainTab('todo');
      setApprovalTypeFilter('财务报销');
    }

    if (!rid || reservations.length === 0) return;
    if (reservationDeepLinkHandledRef.current === rid) return;

    const match = reservations.find((x) => String(x._id) === String(rid));
    const stripReservationId = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('reservationId');
          return next;
        },
        { replace: true }
      );
    };

    if (!match) {
      reservationDeepLinkHandledRef.current = rid;
      stripReservationId();
      setFeedbackModal({ title: '提示', message: '未找到该预约记录，或该预约不属于您负责的仪器。' });
      return;
    }

    reservationDeepLinkHandledRef.current = rid;
    setApprovalMainTab('todo');
    setApprovalTypeFilter('仪器预约');
    setSelectedReservation(match);
    const ins = typeof match.instrumentId === 'object' ? match.instrumentId : null;
    const parts = parseResponsiblePersons(ins?.responsiblePerson);
    const u = localStorage.getItem('username') || '';
    const n = localStorage.getItem('realName') || '';
    const canApprove = parts.length > 0 && (parts.includes(u) || parts.includes(n));
    if (match.status === '待审核' && canApprove) {
      setApprovalForm({ status: '已批准', approvalComment: '' });
    } else {
      setApprovalForm({ status: '已批准', approvalComment: '' });
    }
    setShowReservationModal(true);
    stripReservationId();
  }, [searchParams, reservations, setSearchParams]);

  /** 从通知链接进入：?applicationId=…（可选 tab）→ 打开申请详情 */
  useEffect(() => {
    const aid = searchParams.get('applicationId');
    if (!aid) {
      applicationDeepLinkHandledRef.current = null;
      return;
    }

    const tab = searchParams.get('tab');
    if (tab === 'todo' || tab === 'done' || tab === 'created') {
      setApprovalMainTab(tab);
    }
    if (tab === 'finance') {
      setApprovalMainTab('todo');
      setApprovalTypeFilter('财务报销');
    }
    if (tab === 'reservations') {
      setApprovalMainTab('todo');
      setApprovalTypeFilter('仪器预约');
    }

    const merged = [...pendingApplications, ...processedApplications, ...myApplications, ...financeApplications];
    const seen = new Set<string>();
    const uniq: ApprovalApplication[] = [];
    for (const a of merged) {
      const k = String(a.key);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      uniq.push(a);
    }
    const match = uniq.find((a) => String(a.key) === String(aid));

    const stripApplicationId = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('applicationId');
          return next;
        },
        { replace: true }
      );
    };

    const listTotal =
      financeApplications.length +
      pendingApplications.length +
      processedApplications.length +
      myApplications.length;
    const dataReady = isDemoBrowseOnly || deepLinkListsReady || listTotal > 0;

    if (!match) {
      if (!dataReady) return;
      if (applicationDeepLinkHandledRef.current === aid) return;
      applicationDeepLinkHandledRef.current = aid;
      stripApplicationId();
      setFeedbackModal({ title: '提示', message: '未找到该申请记录，或您无权查看。' });
      return;
    }

    if (applicationDeepLinkHandledRef.current === aid) return;
    applicationDeepLinkHandledRef.current = aid;

    if (!tab || tab === 'finance' || tab === 'reservations') {
      if (pendingApplications.some((a) => String(a.key) === String(aid))) setApprovalMainTab('todo');
      else if (processedApplications.some((a) => String(a.key) === String(aid))) setApprovalMainTab('done');
      else if (myApplications.some((a) => String(a.key) === String(aid))) setApprovalMainTab('created');
      else setApprovalMainTab('todo');
    }

    if (match.category === '财务报销' && (tab === 'finance' || !tab)) {
      setApprovalTypeFilter('财务报销');
    }

    setSelectedTodo(match);
    stripApplicationId();
  }, [
    searchParams,
    pendingApplications,
    processedApplications,
    myApplications,
    financeApplications,
    deepLinkListsReady,
    isDemoBrowseOnly,
    setSearchParams
  ]);

  const baseTableRows = useMemo((): ApprovalMgmtTableRow[] => {
    const approvalRows = (apps: ApprovalApplication[]): ApprovalMgmtTableRow[] =>
      apps.map((application) => ({ kind: 'approval' as const, application }));

    if (approvalMainTab === 'todo') {
      const apps = pendingApplications.filter((app) =>
        ['待审批', '一级审批中', '二级审批中', '三级审批中'].includes(app.status)
      );
      const resv = reservations
        .filter((r) => r.status === '待审核')
        .map((reservation) => ({ kind: 'reservation' as const, reservation }));
      return [...approvalRows(apps), ...resv];
    }
    if (approvalMainTab === 'done') {
      const apps = processedApplications.filter((app) => app.status === '已通过' || app.status === '已驳回');
      const resv = reservations
        .filter((r) => r.status !== '待审核')
        .map((reservation) => ({ kind: 'reservation' as const, reservation }));
      return [...approvalRows(apps), ...resv];
    }
    const apps = myApplications;
    const uid = user?._id || user?.id || localStorage.getItem('userId');
    const resv = reservations
      .filter((r) => {
        if (!uid) return false;
        const u = r.userId;
        let bid = '';
        if (typeof u === 'object' && u && '_id' in u) bid = String((u as { _id?: string })._id || '');
        else if (typeof u === 'string') bid = u;
        return bid === String(uid);
      })
      .map((reservation) => ({ kind: 'reservation' as const, reservation }));
    return [...approvalRows(apps), ...resv];
  }, [approvalMainTab, pendingApplications, processedApplications, myApplications, reservations, user]);

  const filteredTableRows = useMemo((): ApprovalMgmtTableRow[] => {
    if (approvalTypeFilter === '财务报销') {
      let list = financeApplications;
      const uid = user?._id || user?.id || localStorage.getItem('userId');
      if (approvalMainTab === 'todo') {
        list = list.filter((a) =>
          ['待审批', '一级审批中', '二级审批中', '三级审批中'].includes(a.status)
        );
      } else if (approvalMainTab === 'done') {
        list = list.filter((a) => ['已通过', '已驳回'].includes(a.status));
      } else {
        list = list.filter((a) => uid && String(a.applicantId || '') === String(uid));
      }
      return list.map((application) => ({ kind: 'approval' as const, application }));
    }

    if (approvalTypeFilter === '全部') return baseTableRows;
    if (approvalTypeFilter === '仪器预约') {
      return baseTableRows.filter((row) => row.kind === 'reservation');
    }
    return baseTableRows.filter(
      (row) => row.kind === 'approval' && row.application.category === approvalTypeFilter
    );
  }, [approvalTypeFilter, approvalMainTab, baseTableRows, financeApplications, user]);

  const priorityPillClass = (priority: ApprovalApplication['priority']) => {
    if (priority === '特急') return 'am-pill am-pill--pri-high';
    if (priority === '紧急') return 'am-pill am-pill--pri-med';
    return 'am-pill am-pill--pri-low';
  };

  const statusPillClass = (status: ApprovalApplication['status']) => {
    if (status === '待审批' || status === '一级审批中' || status === '二级审批中' || status === '三级审批中') {
      return 'am-pill am-pill--st-pending';
    }
    if (status === '已通过') return 'am-pill am-pill--st-ok';
    if (status === '已驳回') return 'am-pill am-pill--st-reject';
    return 'am-pill am-pill--st-withdraw';
  };

  const reservationStatusPillClass = (status: string) => {
    if (status === '待审核') return 'am-pill am-pill--st-pending';
    if (status === '已批准') return 'am-pill am-pill--st-ok';
    if (status === '已拒绝') return 'am-pill am-pill--st-reject';
    return 'am-pill am-pill--st-withdraw';
  };

  return (
    <div className="page-container full-height members-lims approval-mgmt-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>工作台</span>
        <span className="mm-crumb-sep">/</span>
        <span>审批管理</span>
      </nav>

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">审批管理</h1>
          <p className="mm-subtitle mb-0">
            管理与处理实验室流程审批与仪器预约；可通过「类型筛选」查看不同申请或预约。待办事项中也会列出待处理的文献/实验记录共享邀请。
          </p>
        </div>
      </header>

      <div className="content-area px-1 px-md-2">
        <div className="am-main-tabs">
          {(
            [
              { key: 'todo', label: '待办事项' },
              { key: 'done', label: '已办事项' },
              { key: 'created', label: '我的申请' }
            ] as const
          ).map((t) => (
            <button
              key={t.key}
              type="button"
              className={`am-main-tab${approvalMainTab === t.key ? ' am-main-tab--active' : ''}`}
              onClick={() => {
                setApprovalMainTab(t.key);
                setApprovalTypeFilter('全部');
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="am-table-shell">
            {approvalMainTab === 'todo' && (
              <div id="am-share-invites" className="am-share-invites-block mb-3 p-3 rounded-3 border bg-white">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                  <div>
                    <strong className="d-block">协作共享邀请</strong>
                    <span className="small text-muted">
                      文献管理与实验记录本的共享邀请均在此处理；与顶部通知铃铛、文献管理顶栏铃铛展示同一数据源。
                    </span>
                  </div>
                  <button type="button" className="am-action am-action--ghost" onClick={() => void fetchShareInvites()}>
                    刷新列表
                  </button>
                </div>
                {shareInvitesLoading ? (
                  <p className="small text-muted mb-0">加载中…</p>
                ) : shareInvites.length === 0 ? (
                  <p className="small text-muted mb-0">暂无待处理的共享邀请</p>
                ) : (
                  <ul className="list-unstyled mb-0">
                    {shareInvites.map((n, idx) => {
                      const folderName =
                        n.folder?.name || n.experimentNotebookFolder?.name || '（共享夹）';
                      const kind =
                        n.type === 'shared_experiment_notebook_invite' ? '实验记录共享夹' : '文献共享夹';
                      return (
                        <li
                          key={n._id}
                          className={`d-flex flex-wrap align-items-center justify-content-between gap-2 py-2${idx > 0 ? ' border-top' : ''}`}
                        >
                          <div className="flex-grow-1" style={{ minWidth: 200 }}>
                            <span className="fw-medium">{kind}</span>
                            <span className="text-muted mx-1">·</span>
                            <span>{folderName}</span>
                            <div className="small text-muted">{n.content}</div>
                          </div>
                          <div className="d-flex flex-wrap gap-1">
                            <button
                              type="button"
                              className="am-action am-action--primary"
                              onClick={async () => {
                                try {
                                  if (isDemoBrowseOnly) {
                                    setShareInvites((prev) => prev.filter((x) => x._id !== n._id));
                                    showFeedback('已接受共享邀请（演示模式）', '成功');
                                    return;
                                  }
                                  await literatureNotificationService.acceptInvite(n._id);
                                  showFeedback('已接受共享邀请', '成功');
                                  await fetchShareInvites();
                                  window.dispatchEvent(new CustomEvent('literatureNotificationsUpdated'));
                                } catch (e: unknown) {
                                  showFeedback((e as Error).message || '接受失败', '操作失败');
                                }
                              }}
                            >
                              接受
                            </button>
                            <button
                              type="button"
                              className="am-action am-action--reject"
                              onClick={async () => {
                                try {
                                  if (isDemoBrowseOnly) {
                                    setShareInvites((prev) => prev.filter((x) => x._id !== n._id));
                                    showFeedback('已拒绝该邀请（演示模式）', '成功');
                                    return;
                                  }
                                  await literatureNotificationService.rejectInvite(n._id);
                                  showFeedback('已拒绝该邀请', '成功');
                                  await fetchShareInvites();
                                  window.dispatchEvent(new CustomEvent('literatureNotificationsUpdated'));
                                } catch (e: unknown) {
                                  showFeedback((e as Error).message || '操作失败', '操作失败');
                                }
                              }}
                            >
                              拒绝
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
            <div className="d-flex flex-wrap align-items-center gap-2 justify-content-between mb-2 px-1">
              <span className="small text-muted mb-0" id="am-type-filter-label">
                类型筛选
              </span>
              <Form.Select
                aria-labelledby="am-type-filter-label"
                size="sm"
                className="am-type-filter-select"
                style={{ maxWidth: 240 }}
                value={approvalTypeFilter}
                onChange={(e) => setApprovalTypeFilter(e.target.value as ApprovalTypeFilterValue)}
              >
                {APPROVAL_TYPE_FILTER_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="mm-sheet-scroll">
              <table className="mm-sheet am-sheet">
                <thead>
                  <tr>
                    <th>类型</th>
                    <th>标题</th>
                    <th>申请人</th>
                    <th>发起时间</th>
                    <th>优先级</th>
                    <th>状态</th>
                    <th className="text-end">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTableRows.map((row) => {
                    if (row.kind === 'reservation') {
                      const reservation = row.reservation;
                      const title =
                        typeof reservation.instrumentId === 'object' && reservation.instrumentId
                          ? reservation.instrumentId.name
                          : '仪器预约';
                      const applicant =
                        typeof reservation.userId === 'object' && reservation.userId
                          ? reservation.userId.name
                          : '—';
                      const slotText = `${formatDateTime(reservation.startTime)} – ${formatDateTime(
                        reservation.endTime
                      )}`;
                      return (
                        <tr key={`resv-${reservation._id || String(reservation.startTime)}`} className="mm-row">
                          <td>
                            <div className="am-type-icon">
                              <LimsNavIcon name="precision_manufacturing" sizePx={22} />
                            </div>
                          </td>
                          <td>
                            <span className="am-cell-title d-block">{title}</span>
                            <span className="am-cell-sub d-block">
                              {slotText}
                              {reservation.purpose ? ` · ${reservation.purpose}` : ''}
                            </span>
                          </td>
                          <td>{applicant}</td>
                          <td className="am-cell-muted small">{formatReservationApplyTime(reservation)}</td>
                          <td>
                            <span className="am-pill am-pill--pri-low">—</span>
                          </td>
                          <td>
                            <span className={reservationStatusPillClass(reservation.status)}>
                              {reservation.status}
                            </span>
                          </td>
                          <td className="text-end">
                            <div className="d-flex flex-wrap justify-content-end gap-1">
                              <button
                                type="button"
                                className="am-action am-action--ghost"
                                onClick={() => {
                                  setSelectedReservation(reservation);
                                  setShowReservationModal(true);
                                }}
                              >
                                <EyeOutlined /> 查看
                              </button>
                              {approvalMainTab === 'todo' &&
                                reservation.status === '待审核' &&
                                canCurrentUserApproveReservation(reservation) && (
                                <>
                                  <button
                                    type="button"
                                    className="am-action am-action--primary"
                                    onClick={() => {
                                      setSelectedReservation(reservation);
                                      setApprovalForm({ status: '已批准', approvalComment: '' });
                                      setShowReservationModal(true);
                                    }}
                                  >
                                    批准
                                  </button>
                                  <button
                                    type="button"
                                    className="am-action am-action--reject"
                                    onClick={() => {
                                      setSelectedReservation(reservation);
                                      setApprovalForm({ status: '已拒绝', approvalComment: '' });
                                      setShowReservationModal(true);
                                    }}
                                  >
                                    拒绝
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    const application = row.application;
                    return (
                      <tr key={application.key} className="mm-row">
                        <td>
                          <div className="am-type-icon">
                            <LimsNavIcon name={getCategoryMaterialIcon(application.category)} sizePx={22} />
                          </div>
                        </td>
                        <td>
                          <span className="am-cell-title d-block">{application.title}</span>
                          <span className="am-cell-sub d-block">{getApplicationSubtitle(application)}</span>
                        </td>
                        <td>{application.applicant}</td>
                        <td className="am-cell-muted small">{application.applyTime}</td>
                        <td>
                          <span className={priorityPillClass(application.priority)}>{application.priority}</span>
                        </td>
                        <td>
                          <span className={statusPillClass(application.status)}>{getStatusDisplay(application)}</span>
                        </td>
                        <td className="text-end">
                          <div className="d-flex flex-wrap justify-content-end gap-1">
                            <button
                              type="button"
                              className="am-action am-action--ghost"
                              onClick={() => setSelectedTodo(application)}
                            >
                              <EyeOutlined /> 查看
                            </button>
                            {approvalMainTab === 'todo' &&
                              canActOnApplication(application) && (
                              <>
                                <button
                                  type="button"
                                  className="am-action am-action--primary"
                                  onClick={() => handleApprove(application)}
                                >
                                  {application.merchantOrder?.orderStatus === 'price_pending' ? '确认改价' : '同意'}
                                </button>
                                <button
                                  type="button"
                                  className="am-action am-action--reject"
                                  onClick={() => handleReject(application)}
                                >
                                  {application.merchantOrder?.orderStatus === 'price_pending' ? '驳回改价' : '拒绝'}
                                </button>
                              </>
                            )}
                            {approvalMainTab === 'created' &&
                              ['待审批', '一级审批中', '二级审批中', '三级审批中'].includes(application.status) && (
                              <button
                                type="button"
                                className="am-action am-action--warn"
                                onClick={() => handleWithdraw(application)}
                              >
                                撤回
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredTableRows.length === 0 && (
              <div className="am-empty muted py-5 text-center">
                {approvalMainTab === 'todo' && shareInvites.length > 0 && approvalTypeFilter === '全部'
                  ? '暂无需要您审批的实验室申请或仪器预约'
                  : '暂无记录'}
              </div>
            )}
        </div>
      </div>

      {/* 申请详情查看模态框 */}
      <Modal
        show={!!selectedTodo}
        onHide={() => setSelectedTodo(null)}
        size="lg"
        centered
        backdropClassName="lims-storage-modal-backdrop"
        contentClassName="approval-detail-modal-content"
        dialogClassName="approval-detail-dialog"
      >
        {selectedTodo && (
          <>
            <div className="approval-detail-head">
              <div>
                <h3 className="approval-detail-head-title">申请详情 · {selectedTodo.key}</h3>
                <p className="approval-detail-head-sub">
                  申请人 {selectedTodo.applicant} · {formatRelativeApplyTime(selectedTodo.applyTime)}
                </p>
              </div>
              <button
                type="button"
                className="approval-detail-close"
                aria-label="关闭"
                onClick={() => setSelectedTodo(null)}
              >
                <LimsNavIcon name="close" sizePx={22} />
              </button>
            </div>
            <Modal.Body className="approval-detail-body">
              <div>
              {selectedTodo.category === '财务报销' && (
                <div className="mb-4 p-3 rounded-3 border bg-light">
                  <Row className="g-3">
                    <Col md={6}>
                      <span className="ap-field-overline d-block">报销金额</span>
                      <span className="ap-field-value ap-field-value--accent">
                        {(() => {
                          const cur = selectedTodo.financeReimbursement?.currency || 'CNY';
                          const sym = cur === 'USD' ? '$' : '¥';
                          const n = selectedTodo.totalAmount ?? selectedTodo.financeReimbursement?.claimAmount;
                          return n != null && String(n) !== '' ? `${sym}${Number(n).toFixed(2)}` : '—';
                        })()}
                      </span>
                    </Col>
                    <Col md={6}>
                      <span className="ap-field-overline d-block">费用类型</span>
                      <span className="ap-field-value">{selectedTodo.financeReimbursement?.expenseType || '—'}</span>
                    </Col>
                  </Row>
                  {selectedTodo.financeReimbursement?.attachments &&
                    selectedTodo.financeReimbursement.attachments.length > 0 && (
                      <div className="mt-3">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                          <span className="ap-field-overline d-block mb-0">报销凭证</span>
                          <button
                            type="button"
                            className="approval-detail-btn approval-detail-btn--approve"
                            disabled={financeZipDownloading}
                            onClick={() => void handleDownloadFinanceAttachmentsZip()}
                          >
                            {financeZipDownloading ? '打包中…' : '下载全部附件（ZIP）'}
                          </button>
                        </div>
                        <ul className="list-unstyled mb-0">
                          {selectedTodo.financeReimbursement.attachments.map((att, i) => (
                            <li key={`${att.url}-${i}`} className="py-1">
                              <a
                                href={resolveUploadAssetUrl(att.url) || att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {att.name || `附件 ${i + 1}`}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  {selectedTodo.reason && (
                    <div className="mt-3 pt-3 border-top">
                      <span className="ap-field-overline d-block mb-2">申请说明</span>
                      <p className="mb-0">{selectedTodo.reason}</p>
                    </div>
                  )}
                  {selectedTodo.selectedFinanceApprovers &&
                    selectedTodo.selectedFinanceApprovers.length > 0 && (
                      <div className="mt-3 pt-3 border-top">
                        <span className="ap-field-overline d-block mb-2">报销审批人</span>
                        <p className="mb-0">
                          {selectedTodo.selectedFinanceApprovers
                            .map((t) => t.name || t.id)
                            .filter(Boolean)
                            .join('、')}
                        </p>
                        <div className="text-muted small mt-1">任一人通过即可。</div>
                      </div>
                    )}
                </div>
              )}
              {selectedTodo.merchantOrder?.applyChannel === 'merchant' && (
                <div className="mb-4 p-3 border rounded-3" style={{ background: 'rgba(0, 53, 95, 0.03)' }}>
                  <span className="ap-field-overline d-block mb-2">商家发货 · 收货信息</span>
                  <Row className="g-3">
                    <Col md={4}>
                      <span className="ap-field-overline d-block">商家</span>
                      <span className="ap-field-value">
                        {selectedTodo.merchantOrder.merchantName || '—'}
                      </span>
                    </Col>
                    <Col md={4}>
                      <span className="ap-field-overline d-block">收件人</span>
                      <span className="ap-field-value">
                        {selectedTodo.merchantOrder.recipientName || '—'}
                      </span>
                    </Col>
                    <Col md={4}>
                      <span className="ap-field-overline d-block">手机号</span>
                      <span className="ap-field-value">
                        {selectedTodo.merchantOrder.recipientPhone || '—'}
                      </span>
                    </Col>
                    <Col xs={12}>
                      <span className="ap-field-overline d-block">收货地址</span>
                      <span className="ap-field-value">
                        {selectedTodo.merchantOrder.shippingAddress || '—'}
                      </span>
                    </Col>
                  </Row>
                </div>
              )}
              {selectedTodo.category === '试剂申请' && selectedTodo.reagentProjects?.[0] && (
                <div className="ap-reagent-hero mb-4">
                  <Row className="g-4 align-items-start">
                    <Col md={4}>
                      <div className="ap-reagent-img-frame">
                        <img
                          src={
                            selectedTodo.reagentProjects[0].image
                              ? resolveUploadedImageUrl(selectedTodo.reagentProjects[0].image)
                              : DEFAULT_REAGENT_IMAGE
                          }
                          alt=""
                          className="ap-reagent-img"
                          onClick={() => {
                            const img = selectedTodo.reagentProjects![0].image;
                            if (img) handleImagePreview(img);
                          }}
                          role="presentation"
                        />
                      </div>
                      <div className="ap-hazard-banner">
                        <LimsNavIcon name="warning" sizePx={18} />
                        <span>化学品 · 请按实验室规范存储与使用</span>
                      </div>
                    </Col>
                    <Col md={8}>
                      <span className="ap-field-overline">试剂信息</span>
                      <h4 className="ap-reagent-name">
                        {selectedTodo.reagentProjects[0].name || selectedTodo.title}
                        {selectedTodo.reagentProjects[0].specification
                          ? ` · ${selectedTodo.reagentProjects[0].specification}`
                          : ''}
                      </h4>
                      <div className="ap-reagent-grid">
                        <div>
                          <span className="ap-field-overline d-block">试剂名称</span>
                          <span className="ap-field-value">{selectedTodo.reagentProjects[0].name || '—'}</span>
                        </div>
                        <div>
                          <span className="ap-field-overline d-block">CAS 号</span>
                          <span className="ap-field-value">{selectedTodo.reagentProjects[0].cas}</span>
                        </div>
                        <div>
                          <span className="ap-field-overline d-block">货号</span>
                          <span className="ap-field-value">
                            {selectedTodo.reagentProjects[0].catalogNumber || ''}
                          </span>
                        </div>
                        <div>
                          <span className="ap-field-overline d-block">品牌</span>
                          <span className="ap-field-value">{selectedTodo.reagentProjects[0].vendor}</span>
                        </div>
                        <div>
                          <span className="ap-field-overline d-block">规格</span>
                          <span className="ap-field-value">{selectedTodo.reagentProjects[0].specification}</span>
                        </div>
                        <div>
                          <span className="ap-field-overline d-block">采购渠道</span>
                          <span className="ap-field-value">{selectedTodo.reagentProjects[0].purchaseChannel}</span>
                        </div>
                        <div className="ap-reagent-grid-span">
                          <span className="ap-field-overline d-block">申请数量</span>
                          <span className="ap-field-value ap-field-value--accent">
                            {selectedTodo.reagentProjects[0].amount} {selectedTodo.reagentProjects[0].unit}
                          </span>
                        </div>
                        <div>
                          <span className="ap-field-overline d-block">单价</span>
                          <span className="ap-field-value">
                            {selectedTodo.reagentProjects[0].price
                              ? `¥${selectedTodo.reagentProjects[0].price}`
                              : '—'}
                          </span>
                        </div>
                        <div>
                          <span className="ap-field-overline d-block">总价</span>
                          <span className="ap-field-value ap-field-value--accent">
                            {selectedTodo.reagentProjects[0].total
                              ? `¥${selectedTodo.reagentProjects[0].total}`
                              : '—'}
                          </span>
                        </div>
                      </div>
                      <div className="ap-reason-block mt-4 pt-3">
                        <span className="ap-field-overline d-block mb-2">申请原因</span>
                        <p className="ap-reason-text mb-0">{selectedTodo.reason}</p>
                        {selectedTodo.selectedTeacherApprovers &&
                          selectedTodo.selectedTeacherApprovers.length > 0 && (
                            <div className="mt-3">
                              <span className="ap-field-overline d-block mb-1">教师审批人</span>
                              <p className="mb-0">
                                {selectedTodo.selectedTeacherApprovers
                                  .map((t) => t.name || t.id)
                                  .filter(Boolean)
                                  .join('、')}
                              </p>
                            </div>
                          )}
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              <Row className="mb-3">
                <Col md={6}>
                  <strong>申请类型：</strong>
                  <span className="ms-2 d-inline-flex align-items-center gap-2">
                    <LimsNavIcon name={getCategoryMaterialIcon(selectedTodo.category)} sizePx={20} />
                    {selectedTodo.category}
                  </span>
                </Col>
                <Col md={6}>
                  <strong>申请人：</strong>
                  <span className="ms-2">{selectedTodo.applicant}</span>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>申请时间：</strong>
                  <span className="ms-2">{selectedTodo.applyTime}</span>
                </Col>
                <Col md={6}>
                  <strong>优先级：</strong>
                  <Badge bg={getPriorityColor(selectedTodo.priority)} className="ms-2">
                    {selectedTodo.priority}
                  </Badge>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>状态：</strong>
                  <Badge bg={getStatusColor(selectedTodo.status)} className="ms-2">
                    {selectedTodo.status}
                  </Badge>
                </Col>
              </Row>
              {!(
                selectedTodo.category === '试剂申请' &&
                selectedTodo.reagentProjects &&
                selectedTodo.reagentProjects.length > 0
              ) &&
                selectedTodo.category !== '财务报销' && (
                <Row className="mb-3">
                  <Col md={12}>
                    <strong>申请原因：</strong>
                    <p className="mt-2">{selectedTodo.reason}</p>
                  </Col>
                </Row>
              )}
              {renderReasonImages(selectedTodo)}
              {!(
                selectedTodo.category === '试剂申请' &&
                selectedTodo.reagentProjects &&
                selectedTodo.reagentProjects.length > 0
              ) &&
                (selectedTodo.category === '耗材申请' || selectedTodo.category === '试剂申请') &&
                selectedTodo.selectedTeacherApprovers &&
                selectedTodo.selectedTeacherApprovers.length > 0 && (
                  <Row className="mb-3">
                    <Col md={12}>
                      <strong>教师审批人：</strong>
                      <div className="mt-2">
                        {selectedTodo.selectedTeacherApprovers
                          .map((t) => t.name || t.id)
                          .filter(Boolean)
                          .join('、')}
                      </div>
                      <div className="text-muted small mt-1">
                        已勾选教师：仅需教师审批，任一人通过即可。
                      </div>
                    </Col>
                  </Row>
                )}

              {/* 试剂申请详情（首条已在上方卡片展示） */}
              {selectedTodo.category === '试剂申请' && (selectedTodo.reagentProjects || []).length > 1 && (
                <div className="mb-3">
                  <h6>更多试剂条目</h6>
                  {(selectedTodo.reagentProjects || []).slice(1).map((project, projectIndex) => (
                    <Card key={projectIndex} className="mb-3" style={{ border: '1px solid #e9ecef' }}>
                      <Card.Header className="py-2">
                        <h6 className="mb-0">试剂项目 {projectIndex + 2}</h6>
                      </Card.Header>
                      <Card.Body className="py-3">
                        <Row>
                          <Col md={6}>
                            <strong>试剂分类：</strong>
                            <span className="ms-2">{project.category}</span>
                          </Col>
                          <Col md={6}>
                            <strong>CAS号：</strong>
                            <span className="ms-2">{project.cas}</span>
                          </Col>
                          <Col md={6}>
                            <strong>货号：</strong>
                            <span className="ms-2">{project.catalogNumber || ''}</span>
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col md={6}>
                            <strong>品牌：</strong>
                            <span className="ms-2">{project.vendor}</span>
                          </Col>
                          <Col md={6}>
                            <strong>规格：</strong>
                            <span className="ms-2">{project.specification}</span>
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col md={6}>
                            <strong>单位：</strong>
                            <span className="ms-2">{project.unit}</span>
                          </Col>
                          <Col md={6}>
                            <strong>库存数量：</strong>
                            <span className="ms-2">{project.stockQuantity}</span>
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col md={12}>
                            <strong>存放位置：</strong>
                            <span className="ms-2">{project.storageLocation}</span>
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col md={6}>
                            <strong>购买日期：</strong>
                            <span className="ms-2">{project.purchaseDate}</span>
                          </Col>
                          <Col md={6}>
                            <strong>购买渠道：</strong>
                            <span className="ms-2">{project.purchaseChannel}</span>
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col md={6}>
                            <strong>申请数量：</strong>
                            <span className="ms-2">{project.amount}</span>
                          </Col>
                          <Col md={6}>
                            <strong>单价：</strong>
                            <span className="ms-2">¥{project.price}</span>
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col md={6}>
                            <strong>总价：</strong>
                            <span className="ms-2 text-danger">¥{project.total}</span>
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col md={12}>
                            <strong>使用说明：</strong>
                            <div className="mt-1 p-2 bg-light rounded">
                              {project.usage}
                            </div>
                          </Col>
                        </Row>
                        {project.image && (
                          <div className="mt-3">
                            <strong>试剂图片：</strong>
                            <div className="mt-2">
                              <img 
                                src={resolveUploadedImageUrl(project.image)} 
                                alt="试剂图片" 
                                style={{ width: '100px', height: '100px', objectFit: 'cover', cursor: 'pointer', borderRadius: '8px' }}
                                onClick={() => handleImagePreview(project.image!)}
                              />
                            </div>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                  
                  {/* 显示总金额 */}
                  {selectedTodo.reagentProjects && selectedTodo.reagentProjects.length > 0 && (
                    <div className="mt-3 p-3 bg-primary bg-opacity-10 rounded">
                      <Row>
                        <Col md={12}>
                          <strong className="text-primary">申请总金额：</strong>
                          <span className="ms-2 text-primary fw-bold fs-5">
                            ¥{selectedTodo.reagentProjects.reduce((sum, project) => {
                              return sum + (parseFloat(project.total || '0'));
                            }, 0).toFixed(2)}
                          </span>
                        </Col>
                      </Row>
                    </div>
                  )}
                </div>
              )}

              {/* 耗材申请详情 */}
              {selectedTodo.category === '耗材申请' && (
                <div className="mb-3">
                  <h6>耗材信息</h6>
                  {selectedTodo.consumableProjects?.map((project, projectIndex) => (
                    <Card key={projectIndex} className="mb-3" style={{ border: '1px solid #e9ecef' }}>
                      <Card.Header className="py-2">
                        <h6 className="mb-0">耗材项目 {projectIndex + 1}</h6>
                      </Card.Header>
                      <Card.Body className="py-3">
                        <Row>
                          <Col md={6}>
                            <strong>耗材名称：</strong>
                            <span className="ms-2">{project.name}</span>
                          </Col>
                          <Col md={6}>
                            <strong>型号：</strong>
                            <span className="ms-2">{project.model}</span>
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col md={6}>
                            <strong>品牌：</strong>
                            <span className="ms-2">{project.brand || ''}</span>
                          </Col>
                          <Col md={6}>
                            <strong>单位：</strong>
                            <span className="ms-2">{project.unit}</span>
                          </Col>
                          <Col md={6}>
                            <strong>库存数量：</strong>
                            <span className="ms-2">{project.stockQuantity}</span>
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col md={12}>
                            <strong>存放位置：</strong>
                            <span className="ms-2">{project.storageLocation}</span>
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col md={6}>
                            <strong>购买日期：</strong>
                            <span className="ms-2">{project.purchaseDate}</span>
                          </Col>
                          <Col md={6}>
                            <strong>购买渠道：</strong>
                            <span className="ms-2">{project.purchaseChannel}</span>
                          </Col>
                        </Row>
                        {project.purchaseChannel === '网购' && (
                          <Row className="mt-2">
                            <Col md={12}>
                              <strong>购买链接：</strong>
                              <span className="ms-2">
                                <a href={project.purchaseLink} target="_blank" rel="noopener noreferrer">
                                  {project.purchaseLink}
                                </a>
                              </span>
                            </Col>
                          </Row>
                        )}
                        {project.purchaseChannel === '其他' && (
                          <Row className="mt-2">
                            <Col md={12}>
                              <strong>渠道商：</strong>
                              <span className="ms-2">{project.supplier}</span>
                            </Col>
                          </Row>
                        )}
                        <Row className="mt-2">
                          <Col md={6}>
                            <strong>申请数量：</strong>
                            <span className="ms-2">{project.amount}</span>
                          </Col>
                          <Col md={6}>
                            <strong>单价：</strong>
                            <span className="ms-2">¥{project.price}</span>
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col md={6}>
                            <strong>总价：</strong>
                            <span className="ms-2 text-danger">¥{project.total}</span>
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col md={12}>
                            <strong>使用说明：</strong>
                            <div className="mt-1 p-2 bg-light rounded">
                              {project.usage}
                            </div>
                          </Col>
                        </Row>
                        <div className="mt-3">
                          <strong>耗材图片：</strong>
                          <div className="mt-2">
                            <img
                              src={
                                project.image
                                  ? resolveUploadedImageUrl(project.image)
                                  : DEFAULT_CONSUMABLE_IMAGE
                              }
                              alt="耗材图片"
                              style={{ width: '100px', height: '100px', objectFit: 'cover', cursor: 'pointer', borderRadius: '8px' }}
                              onClick={() => {
                                if (project.image) handleImagePreview(project.image);
                              }}
                            />
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                  
                  {/* 显示总金额 */}
                  {selectedTodo.consumableProjects && selectedTodo.consumableProjects.length > 0 && (
                    <div className="mt-3 p-3 bg-primary bg-opacity-10 rounded">
                      <Row>
                        <Col md={12}>
                          <strong className="text-primary">申请总金额：</strong>
                          <span className="ms-2 text-primary fw-bold fs-5">
                            ¥{selectedTodo.consumableProjects.reduce((sum, project) => {
                              return sum + (parseFloat(project.total || '0'));
                            }, 0).toFixed(2)}
                          </span>
                        </Col>
                      </Row>
                    </div>
                  )}
                </div>
              )}

              {(selectedTodo.category === '用章申请' || selectedTodo.category === '物品报修') && (
                <div className="mb-3">
                  <h6>{selectedTodo.category === '用章申请' ? '用章申请概要' : '物品报修概要'}</h6>
                  <Row className="mt-2">
                    <Col md={12}>
                      <strong>申请标题：</strong>
                      <span className="ms-2">{selectedTodo.title}</span>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col md={6}>
                      <strong>优先级：</strong>
                      <span className="ms-2">{selectedTodo.priority || '—'}</span>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col md={12}>
                      <strong>事由说明：</strong>
                      <div className="mt-1 p-2 bg-light rounded">{selectedTodo.reason || '—'}</div>
                    </Col>
                  </Row>
                </div>
              )}

              {/* 请假申请详情 */}
              {selectedTodo.category === '请假' && (
                <div className="mb-3">
                  <h6>请假信息</h6>
                  <Row>
                    <Col md={6}>
                      <strong>开始时间：</strong>
                      <span className="ms-2">{selectedTodo.leaveStart}</span>
                    </Col>
                    <Col md={6}>
                      <strong>结束时间：</strong>
                      <span className="ms-2">{selectedTodo.leaveEnd}</span>
                    </Col>
                  </Row>
                </div>
              )}

              {/* 测试申请详情 */}
              {selectedTodo.category === '测试申请' && (
                <div className="mb-3">
                  <h6>测试信息</h6>
                  <Row>
                    <Col md={12}>
                      <strong>测试平台：</strong>
                      <span className="ms-2">{selectedTodo.testPlatform}</span>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md={12}>
                      <strong>测试项目：</strong>
                      <div className="mt-2">
                        {selectedTodo.testProjects?.map((project, index) => (
                          <Card key={index} className="mb-2" style={{ border: '1px solid #e9ecef' }}>
                            <Card.Body className="py-2">
                              <Row>
                                <Col md={6}>
                                  <strong>项目名称：</strong>
                                  <span className="ms-2">{project.project}</span>
                                </Col>
                                <Col md={3}>
                                  <strong>单价：</strong>
                                  <span className="ms-2">¥{project.unitPrice}</span>
                                </Col>
                                <Col md={3}>
                                  <strong>数量：</strong>
                                  <span className="ms-2">{project.quantity}</span>
                                </Col>
                              </Row>
                              <Row className="mt-1">
                                <Col md={12}>
                                  <strong>项目总金额：</strong>
                                  <span className="ms-2 text-primary fw-bold">¥{project.totalAmount}</span>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col md={12}>
                      <strong>申请总金额：</strong>
                      <span className="ms-2 text-primary fw-bold fs-5">¥{selectedTodo.testTotalAmount}</span>
                    </Col>
                  </Row>
                </div>
              )}

              {/* 出差申请详情 */}
              {selectedTodo.category === '出差申请' && (
                <div className="mb-3">
                  <h6>出差信息</h6>
                  <Row>
                    <Col md={6}>
                      <strong>出差目的地：</strong>
                      <span className="ms-2">{selectedTodo.destination}</span>
                    </Col>
                    <Col md={6}>
                      <strong>出差天数：</strong>
                      <span className="ms-2">{selectedTodo.tripDays} 天</span>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col md={6}>
                      <strong>出发日期：</strong>
                      <span className="ms-2">{selectedTodo.tripStartDate}</span>
                    </Col>
                    <Col md={6}>
                      <strong>返回日期：</strong>
                      <span className="ms-2">{selectedTodo.tripEndDate}</span>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col md={6}>
                      <strong>预算金额：</strong>
                      <span className="ms-2 text-primary fw-bold">¥{selectedTodo.tripBudget}</span>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col md={12}>
                      <strong>出差事由：</strong>
                      <div className="mt-1 p-2 bg-light rounded">
                        {selectedTodo.tripPurpose}
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {/* 审批记录 */}
              <div className="mb-3">
                <h6>审批记录</h6>
                <ListGroup>
                  {selectedTodo.approvalRecords?.map((record, index) => (
                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{record.node}</strong>
                        <div className="text-muted small">
                          {record.approver} · {record.time}
                        </div>
                      </div>
                      <Badge bg={record.result === '同意' ? 'success' : record.result === '拒绝' ? 'danger' : 'secondary'}>
                        {record.result}
                      </Badge>
                    </ListGroup.Item>
                  )) || (
                    <ListGroup.Item>
                      <div className="text-muted text-center">暂无审批记录</div>
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </div>
            </div>
            </Modal.Body>
            <div className="approval-detail-foot">
              <button type="button" className="approval-detail-btn approval-detail-btn--ghost" onClick={() => setSelectedTodo(null)}>
                关闭
              </button>
              {approvalMainTab === 'todo' &&
                canActOnApplication(selectedTodo) && (
                <>
                  <button
                    type="button"
                    className="approval-detail-btn approval-detail-btn--reject"
                    onClick={() => {
                      setApprovalTargetKey(selectedTodo.key);
                      setPriceConfirmMode(selectedTodo.merchantOrder?.orderStatus === 'price_pending');
                      setShowApprovalModal(true);
                      setSelectedTodo(null);
                    }}
                  >
                    <CloseOutlined className="me-1" />
                    {selectedTodo.merchantOrder?.orderStatus === 'price_pending' ? '驳回改价' : '驳回申请'}
                  </button>
                  <button
                    type="button"
                    className="approval-detail-btn approval-detail-btn--approve"
                    onClick={() => {
                      setApprovalTargetKey(selectedTodo.key);
                      setPriceConfirmMode(selectedTodo.merchantOrder?.orderStatus === 'price_pending');
                      setShowApprovalModal(true);
                      setSelectedTodo(null);
                    }}
                  >
                    <CheckOutlined className="me-1" />
                    {selectedTodo.merchantOrder?.orderStatus === 'price_pending' ? '确认改价' : '同意申请'}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* 审批操作模态框 */}
      <Modal
        show={showApprovalModal}
        onHide={() => {
          setShowApprovalModal(false);
          setApprovalTargetKey(null);
          setPriceConfirmMode(false);
        }}
        centered
        backdrop="static"
        backdropClassName="lims-storage-modal-backdrop"
        dialogClassName="lims-approval-flow-modal__dialog"
        contentClassName="lims-approval-flow-modal__content"
      >
        <Modal.Header closeButton className="lims-approval-flow-modal__header border-0 pb-0">
          <Modal.Title as="h2" className="lims-approval-flow-modal__title mb-0">
            {priceConfirmMode ? '审批商家改价' : '审批操作'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="lims-approval-flow-modal__body">
          {priceConfirmMode && (() => {
            const app = [...pendingApplications, ...myApplications].find(
              (a) => a.key === approvalTargetKey
            );
            const mo = app?.merchantOrder;
            if (!mo) return null;
            const from =
              mo.quotedAmount != null
                ? Number(mo.quotedAmount)
                : mo.originalAmount != null
                  ? Number(mo.originalAmount)
                  : null;
            const to = mo.pendingQuotedAmount != null ? Number(mo.pendingQuotedAmount) : null;
            return (
              <div className="alert alert-warning py-2 small mb-3">
                商家改价申请：请重新审批实付价格
                {from != null && to != null
                  ? `（¥${from.toFixed(2)} → ¥${to.toFixed(2)}）`
                  : to != null
                    ? `（拟改价 ¥${to.toFixed(2)}）`
                    : ''}
                。通过后订单回到已采购/待发货；驳回后商家可按原价发货或再次改价。
              </div>
            );
          })()}
          <label className="lims-approval-flow-modal__label" htmlFor="approval-flow-comment">
            审批意见
          </label>
          <textarea
            id="approval-flow-comment"
            className="lims-storage-modal__input"
            rows={3}
            value={approvalComment}
            onChange={(e) => setApprovalComment(e.target.value)}
            placeholder="选填：填写通过或驳回的补充说明"
          />
        </Modal.Body>
        <Modal.Footer className="lims-approval-flow-modal__footer border-0 d-flex justify-content-end flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-link lims-storage-modal__btn-ghost text-decoration-none"
            onClick={() => {
              setShowApprovalModal(false);
              setApprovalTargetKey(null);
            }}
          >
            取消
          </button>
          <button
            type="button"
            className="lims-approval-flow-modal__btn-reject"
            onClick={() => {
              if (approvalTargetKey) {
                handleApprovalAction(approvalTargetKey, '驳回', approvalComment);
              }
              setApprovalTargetKey(null);
            }}
          >
            <CloseOutlined />
            驳回
          </button>
          <button
            type="button"
            className="lims-approval-flow-modal__btn-primary"
            onClick={() => {
              if (approvalTargetKey) {
                handleApprovalAction(approvalTargetKey, '通过', approvalComment);
              }
              setApprovalTargetKey(null);
            }}
          >
            <CheckOutlined />
            通过
          </button>
        </Modal.Footer>
      </Modal>

      {/* 图片放大弹窗 */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        centered
        size="lg"
        backdropClassName="lims-storage-modal-backdrop"
        dialogClassName="lims-approval-image-modal__dialog"
        contentClassName="lims-approval-image-modal__content"
      >
        <Modal.Header closeButton className="lims-approval-image-modal__head">
          <Modal.Title>图片预览</Modal.Title>
        </Modal.Header>
        <Modal.Body className="lims-approval-image-modal__body">
          {modalImageUrl && (
            <img src={modalImageUrl} alt="大图" style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
          )}
        </Modal.Body>
      </Modal>

      {/* 仪器预约审批模态框 */}
      <Modal
        show={showReservationModal}
        onHide={() => setShowReservationModal(false)}
        size="lg"
        centered
        backdropClassName="lims-storage-modal-backdrop"
        dialogClassName="lims-approval-flow-modal__dialog lims-approval-flow-modal__dialog--lg"
        contentClassName="lims-approval-flow-modal__content"
      >
        <Modal.Header closeButton className="lims-approval-flow-modal__header border-0 pb-0">
          <Modal.Title as="h2" className="lims-approval-flow-modal__title mb-0">
            {selectedReservation &&
              `仪器预约审批 · ${
                typeof selectedReservation.instrumentId === 'object' && selectedReservation.instrumentId
                  ? selectedReservation.instrumentId.name
                  : '仪器预约'
              }`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="lims-approval-flow-modal__body lims-approval-flow-modal__body--flush-top">
          {selectedReservation && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>仪器名称：</strong>
                  <span className="ms-2">
                    {typeof selectedReservation.instrumentId === 'object' && selectedReservation.instrumentId
                      ? selectedReservation.instrumentId.name 
                      : '未知仪器'}
                  </span>
                </Col>
                <Col md={6}>
                  <strong>申请人：</strong>
                  <span className="ms-2">
                    {typeof selectedReservation.userId === 'object' && selectedReservation.userId
                      ? selectedReservation.userId.name 
                      : '未知用户'}
                  </span>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>预约时间：</strong>
                  <span className="ms-2">
                    {formatDateTime(selectedReservation.startTime)} - {formatDateTime(selectedReservation.endTime)}
                  </span>
                </Col>
                <Col md={6}>
                  <strong>发起时间：</strong>
                  <span className="ms-2">{formatReservationApplyTime(selectedReservation)}</span>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>当前状态：</strong>
                  <Badge bg={getReservationStatusColor(selectedReservation.status)} className="ms-2">
                    {selectedReservation.status}
                  </Badge>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <strong>使用目的：</strong>
                  <p className="mt-2">{selectedReservation.purpose}</p>
                </Col>
              </Row>
              {selectedReservation.approvalComment && (
                <Row className="mb-3">
                  <Col md={12}>
                    <strong>审批意见：</strong>
                    <p className="mt-2">{selectedReservation.approvalComment}</p>
                  </Col>
                </Row>
              )}
              {selectedReservation.status === '待审核' && canCurrentUserApproveReservation(selectedReservation) && (
                <div className="mt-4 pt-3 border-top border-light-subtle">
                  <p className="lims-approval-flow-modal__label mb-2">仪器预约 · 审批</p>
                  <Form>
                    <Form.Group className="mb-3">
                      <span className="lims-approval-flow-modal__label">审批结果</span>
                      <Form.Select
                        className="lims-approval-flow-modal__select"
                        value={approvalForm.status}
                        onChange={(e) =>
                          setApprovalForm({
                            ...approvalForm,
                            status: e.target.value as '已批准' | '已拒绝'
                          })
                        }
                      >
                        <option value="已批准">批准</option>
                        <option value="已拒绝">拒绝</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-0">
                      <label className="lims-approval-flow-modal__label" htmlFor="reservation-approval-comment">
                        审批意见
                      </label>
                      <textarea
                        id="reservation-approval-comment"
                        className="lims-storage-modal__input"
                        rows={3}
                        value={approvalForm.approvalComment}
                        onChange={(e) => setApprovalForm({ ...approvalForm, approvalComment: e.target.value })}
                        placeholder="选填"
                      />
                    </Form.Group>
                  </Form>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="lims-approval-flow-modal__footer border-0 d-flex justify-content-end flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-link lims-storage-modal__btn-ghost text-decoration-none"
            onClick={() => setShowReservationModal(false)}
          >
            关闭
          </button>
          {selectedReservation &&
            selectedReservation.status === '待审核' &&
            canCurrentUserApproveReservation(selectedReservation) && (
              <button type="button" className="lims-approval-flow-modal__btn-primary" onClick={handleApproveReservation}>
                确定审批
              </button>
            )}
        </Modal.Footer>
      </Modal>

      <LimsFeedbackModal
        show={!!feedbackModal}
        onHide={() => setFeedbackModal(null)}
        title={feedbackModal?.title}
        message={feedbackModal?.message ?? ''}
      />
    </div>
  );
};

export default ApprovalManagement;
